import { useMemo, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'
import { useLoader, Container, useWindowSize } from '@moneymade/moneymade-ui'

import NotFound from 'components/ui/NotFound/NotFound'
import styles from './SoloPlatformReviewPage.module.scss'

const TopBlock = dynamic(() => import('modules/learn/SoloPlatformReviewPage/TopBlock/TopBlock'))
const GoodFor = dynamic(() => import('modules/learn/SoloPlatformReviewPage/GoodFor/GoodFor'))
const ContentBlock = dynamic(() => import('modules/learn/SoloPlatformReviewPage/ContentBlock/ContentBlock'))
const SignUpFaq = dynamic(() => import('modules/learn/SoloPlatformReviewPage/SignUpFaq/SignUpFaq'))
const OtherApps = dynamic(() => import('modules/learn/SoloPlatformReviewPage/OtherApps/OtherApps'))
const ProsCons = dynamic(() => import('modules/learn/SoloPlatformReviewPage/ProsCons/ProsCons'))
const CheetSheet = dynamic(() => import('modules/learn/SoloPlatformReviewPage/CheetSheet/CheetSheet'))
const Navigation = dynamic(() => import('modules/learn/SoloPlatformReviewPage/Navigation/Navigation'))
const Share = dynamic(() => import('modules/collections/CollectionPage/Share/Share'))
const Author = dynamic(() => import('modules/learn/SoloPlatformReviewPage/Author/Author'))
const FaqBlock = dynamic(() => import('modules/learn/SoloPlatformReviewPage/FaqBlock/FaqBlock'))
const AssetBlock = dynamic(() => import('modules/learn/SoloPlatformReviewPage/AssetBlock/AssetBlock'))
const PlatformCard = dynamic(() => import('modules/learn/SoloPlatformReviewPage/PlatformCard/PlatformCard'))
const TextBlock = dynamic(() => import('modules/learn/SoloPlatformReviewPage/TextBlock/TextBlock'))
// const SubscribeBlock = dynamic(() => import('modules/learn/SoloPlatformReviewPage/SubscribeBlock/SubscribeBlock'))

const NAVIGATION_LIST_NAMES = {
  cheetChat: 'cheetChat',
  prosCons: 'prosCons',
  whatIs: 'whatIs',
  whereGetsItRight: 'whereGetsItRight',
  whereCouldDoBetter: 'whereCouldDoBetter',
  canYouReallyMakeMoneyOn: 'canYouReallyMakeMoneyOn',
  whatOtherPeopleAreSayingAbout: 'whatOtherPeopleAreSayingAbout',
  areThereOtherAppsLike: 'areThereOtherAppsLike',
  ourHotTakeOn: 'ourHotTakeOn',
  howDoISignUpFor: 'howDoISignUpFor',
  faq: 'faq',
  asset: 'asset'
}

const getTitle = (slug, navigationList) =>
  navigationList.find(({ slug: slugLocal }) => slugLocal === slug)?.labelContent

const SoloPlatformReviewPage = ({
  inIframe,
  review: {
    name,
    slug,
    title,
    authors,
    cardColor,
    description,
    introduction,
    prosCons,
    cheetChat,
    image,
    published,
    updated,
    goodFor,
    whatIs,
    whereGetsItRight,
    whereCouldDoBetter,
    canYouReallyMakeMoneyOn,
    whatOtherPeopleAreSayingAbout,
    areThereOtherAppsLike,
    ourHotTakeOn,
    howDoISignUpFor,
    platform,
    faq,
    asset,
    textBlock,
    investment
  },
  review,
  reviews,
  authors: linkAuthors
}) => {
  const { loaderElement } = useLoader(true)
  const { screenWidth } = useWindowSize()
  const { isFallback } = useRouter()
  const [stickyHeader, setStickyHeader] = useState(false)
  useEffect(() => {
    if (!document.querySelector('[src="https://moneymade.activehosted.com/f/embed.php?id=1"]')) {
      const script = document.createElement('script')

      script.src = 'https://moneymade.activehosted.com/f/embed.php?id=1'
      script.type = 'text/javascript'
      script.charSet = 'utf-8'

      document.head.appendChild(script)

      return () => document.head.removeChild(script)
    }
  }, [])

  // Replace snippets with widget component
  useEffect(() => {
    const snippets = document.querySelectorAll('.snippet')

    snippets.forEach(snippet => {
      // Expecting: {"symbol": "APPL,GOOG", "timeline": "1Y"}
      const { symbol, timeline } = JSON.parse(snippet.innerText)

      // Create iframe
      const iframe = document.createElement('iframe')
      iframe.src = `https://one-widget.vercel.app/ticker-table?symbol=${symbol}&timeframe=${timeline}`
      iframe.width = '100%'
      iframe.height = '385px'
      iframe.style.border = 'none'
      iframe.loading = 'lazy'
      iframe.title = symbol

      // Append iframe
      const parent = snippet.parentElement
      parent.removeChild(snippet)
      parent.appendChild(iframe)
    })
  }, [])

  const handleStickyChange = isVisible => (isVisible ? setStickyHeader(false) : setStickyHeader(true))

  const navigationList = useMemo(() => {
    const tempArray = []

    tempArray.push({
      id: 0,
      slug: NAVIGATION_LIST_NAMES.cheetChat,
      labelContent: cheetChat?.title || 'Cheat Sheet',
      label: cheetChat?.titleTOC || cheetChat?.title || 'Cheat Sheet'
    })

    if (prosCons)
      tempArray.push({
        id: 1,
        slug: NAVIGATION_LIST_NAMES.prosCons,
        labelContent: prosCons?.title || 'Pros and cons',
        label: prosCons?.titleTOC || prosCons?.title || 'Pros and cons'
      })

    if (whatIs)
      tempArray.push({
        id: 2,
        slug: NAVIGATION_LIST_NAMES.whatIs,
        labelContent: whatIs?.title || `What is ${name}?`,
        label: whatIs?.titleTOC || whatIs?.title || `What is ${name}?`
      })

    if (whereGetsItRight)
      tempArray.push({
        id: 3,
        slug: NAVIGATION_LIST_NAMES.whereGetsItRight,
        labelContent: whereGetsItRight?.title || `Where ${name} gets it right?`,
        label: whereGetsItRight?.titleTOC || whereGetsItRight?.title || `Where ${name} gets it right?`
      })

    if (whereCouldDoBetter)
      tempArray.push({
        id: 4,
        slug: NAVIGATION_LIST_NAMES.whereCouldDoBetter,
        labelContent: whereCouldDoBetter?.title || `Where ${name} could do better?`,
        label: whereCouldDoBetter?.titleTOC || whereCouldDoBetter?.title || `Where ${name} could do better?`
      })

    if (canYouReallyMakeMoneyOn)
      tempArray.push({
        id: 5,
        slug: NAVIGATION_LIST_NAMES.canYouReallyMakeMoneyOn,
        labelContent: canYouReallyMakeMoneyOn?.title || `Can you really make money on ${name}?`,
        label:
          canYouReallyMakeMoneyOn?.titleTOC || canYouReallyMakeMoneyOn?.title || `Can you really make money on ${name}?`
      })

    if (whatOtherPeopleAreSayingAbout?.title)
      tempArray.push({
        id: 6,
        slug: NAVIGATION_LIST_NAMES.whatOtherPeopleAreSayingAbout,
        labelContent: whatOtherPeopleAreSayingAbout?.title || `What other people are saying about ${name}?`,
        label:
          whatOtherPeopleAreSayingAbout?.titleTOC ||
          whatOtherPeopleAreSayingAbout?.title ||
          `What other people are saying about ${name}?`
      })

    if (areThereOtherAppsLike?.title)
      tempArray.push({
        id: 7,
        slug: NAVIGATION_LIST_NAMES.areThereOtherAppsLike,
        labelContent: areThereOtherAppsLike?.title || `Are there other apps like ${name}?`,
        label: areThereOtherAppsLike?.titleTOC || areThereOtherAppsLike?.title || `Are there other apps like ${name}?`
      })

    if (ourHotTakeOn)
      tempArray.push({
        id: 8,
        slug: NAVIGATION_LIST_NAMES.ourHotTakeOn,
        labelContent: ourHotTakeOn?.title || `Our hot take on ${name}`,
        label: ourHotTakeOn?.titleTOC || ourHotTakeOn?.title || `Our hot take on ${name}`
      })

    if (howDoISignUpFor)
      tempArray.push({
        id: 9,
        slug: NAVIGATION_LIST_NAMES.howDoISignUpFor,
        labelContent: howDoISignUpFor?.title || `I’m in! How do I sign up for ${name}?`,
        label: howDoISignUpFor?.titleTOC || howDoISignUpFor?.title || `I’m in! How do I sign up for ${name}?`
      })

    if (faq)
      tempArray.push({
        id: 13,
        slug: NAVIGATION_LIST_NAMES.faq,
        labelContent: faq?.title || `FAQ`,
        label: faq?.titleTOC || faq?.title || `FAQ`
      })

    return tempArray
  }, [
    cheetChat?.title,
    cheetChat?.titleTOC,
    prosCons,
    whatIs,
    name,
    whereGetsItRight,
    whereCouldDoBetter,
    canYouReallyMakeMoneyOn,
    whatOtherPeopleAreSayingAbout,
    areThereOtherAppsLike,
    ourHotTakeOn,
    howDoISignUpFor,

    faq
  ])

  return !isFallback ? (
    <section className={styles.SoloPlatformReviewPage}>
      {Object.keys(review)?.length ? (
        <>
          <TopBlock
            key={`${slug}_0`}
            review={{ name, title, cardColor, description, image }}
            handleStickyChange={handleStickyChange}
            investment={investment}
          />

          <Container width={screenWidth > 1000 ? 1000 : 600} className={styles.Container}>
            <div className={styles.Left}>
              {authors?.map(({ imageAuthor, slug: slugAuthor, name: nameAuthor }, index) => (
                <Author
                  imageAuthor={imageAuthor}
                  name={nameAuthor}
                  key={index}
                  slug={slugAuthor}
                  article={{ published, updated }}
                  authors={linkAuthors}
                />
              ))}

              {goodFor && !!goodFor.length && <GoodFor key={`${slug}_1`} icons={goodFor} />}

              {introduction && <ContentBlock key={`${slug}_2`} text={introduction} id="introduction" />}

              {platform?.slugPlatform !== 'domain-money' ? <PlatformCard key={`${slug}_2_0`} {...platform} /> : null}

              <CheetSheet
                key={`${slug}_2_1`}
                title={getTitle(NAVIGATION_LIST_NAMES.cheetChat, navigationList)}
                guid={NAVIGATION_LIST_NAMES.cheetChat}
                investment={investment}
              />

              {prosCons && (
                <ProsCons
                  key={`${slug}_2_2`}
                  pros={prosCons?.pros}
                  cons={prosCons?.cons}
                  title={getTitle(NAVIGATION_LIST_NAMES.prosCons, navigationList)}
                  guid={NAVIGATION_LIST_NAMES.prosCons}
                />
              )}

              {whatIs && (
                <ContentBlock
                  key={`${slug}_3`}
                  text={whatIs?.text}
                  title={getTitle(NAVIGATION_LIST_NAMES.whatIs, navigationList)}
                  guid={NAVIGATION_LIST_NAMES.whatIs}
                />
              )}

              {whereGetsItRight && (
                <ContentBlock
                  key={`${slug}_4`}
                  text={whereGetsItRight?.text}
                  title={getTitle(NAVIGATION_LIST_NAMES.whereGetsItRight, navigationList)}
                  guid={NAVIGATION_LIST_NAMES.whereGetsItRight}
                />
              )}

              {whereCouldDoBetter && (
                <ContentBlock
                  key={`${slug}_5`}
                  text={whereCouldDoBetter?.text}
                  title={getTitle(NAVIGATION_LIST_NAMES.whereCouldDoBetter, navigationList)}
                  guid={NAVIGATION_LIST_NAMES.whereCouldDoBetter}
                />
              )}

              {canYouReallyMakeMoneyOn && (
                <ContentBlock
                  key={`${slug}_6`}
                  text={canYouReallyMakeMoneyOn?.text}
                  title={getTitle(NAVIGATION_LIST_NAMES.canYouReallyMakeMoneyOn, navigationList)}
                  guid={NAVIGATION_LIST_NAMES.canYouReallyMakeMoneyOn}
                />
              )}

              {platform?.slugPlatform !== 'domain-money' ? <PlatformCard key={`${slug}_2_0`} {...platform} /> : null}

              {whatOtherPeopleAreSayingAbout && (
                <ContentBlock
                  key={`${slug}_8`}
                  text={whatOtherPeopleAreSayingAbout?.text}
                  title={getTitle(NAVIGATION_LIST_NAMES.whatOtherPeopleAreSayingAbout, navigationList)}
                  guid={NAVIGATION_LIST_NAMES.whatOtherPeopleAreSayingAbout}
                />
              )}

              {areThereOtherAppsLike && (
                <OtherApps
                  key={`${slug}_9`}
                  title={getTitle(NAVIGATION_LIST_NAMES.areThereOtherAppsLike, navigationList)}
                  guid={NAVIGATION_LIST_NAMES.areThereOtherAppsLike}
                  apps={areThereOtherAppsLike}
                  investment={investment}
                  reviews={reviews}
                />
              )}

              {ourHotTakeOn && (
                <ContentBlock
                  key={`${slug}_10`}
                  text={ourHotTakeOn?.text}
                  title={getTitle(NAVIGATION_LIST_NAMES.ourHotTakeOn, navigationList)}
                  guid={NAVIGATION_LIST_NAMES.ourHotTakeOn}
                />
              )}

              {howDoISignUpFor && (
                <SignUpFaq
                  key={`${slug}_11`}
                  faq={howDoISignUpFor?.faq}
                  title={getTitle(NAVIGATION_LIST_NAMES.howDoISignUpFor, navigationList)}
                  guid={NAVIGATION_LIST_NAMES.howDoISignUpFor}
                />
              )}

              {platform?.slugPlatform !== 'domain-money' ? <PlatformCard key={`${slug}_2_0`} {...platform} /> : null}

              {/* {!isLogged && subscribeBlock && Object.keys(subscribeBlock).length ? ( 
                <SubscribeBlock key={`${slug}_123`} {...subscribeBlock} />
              ) : null}
              */}

              {textBlock && <TextBlock {...textBlock} />}

              {faq && (
                <FaqBlock
                  faq={faq?.faq}
                  key={`${slug}_15`}
                  guid={NAVIGATION_LIST_NAMES.faq}
                  title={getTitle(NAVIGATION_LIST_NAMES.faq, navigationList)}
                />
              )}

              {asset && (
                <div id={NAVIGATION_LIST_NAMES.asset}>
                  <div id={NAVIGATION_LIST_NAMES.asset}>
                    <AssetBlock
                      key={`${slug}_16`}
                      title={asset?.data?.title}
                      guid={NAVIGATION_LIST_NAMES.asset}
                      subTitle={asset?.data?.subTitle}
                      tags={asset.tags}
                    />
                  </div>
                </div>
              )}
            </div>

            {!inIframe && (
              <div className={styles.Right}>
                <Navigation
                  stickyHeader={stickyHeader}
                  navigationList={navigationList}
                  name={investment?.name}
                  slug={investment?.slug}
                  website={investment?.website}
                />
              </div>
            )}
          </Container>

          {/* {featuredDeals && <FeaturedDeals investment={investment} />} */}

          <div id="Share" />

          <Container width={screenWidth > 1000 ? 1000 : 600} id="Share" className={styles.Share}>
            <Share />
          </Container>
        </>
      ) : (
        <NotFound text="Review not found" />
      )}
    </section>
  ) : (
    <div className={styles.Loading}>{loaderElement}</div>
  )
}

SoloPlatformReviewPage.propTypes = {
  inIframe: PropTypes.bool,
  review: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    slug: PropTypes.string,
    authors: PropTypes.arrayOf(PropTypes.object),
    title: PropTypes.string,
    textBlock: PropTypes.shape({
      text: PropTypes.string,
      images: PropTypes.string
    }),

    ticker: PropTypes.shape({
      slug: PropTypes.string,
      className: PropTypes.string,
      exchange: PropTypes.string,
      ticker: PropTypes.string
    }),
    asset: PropTypes.shape({
      data: PropTypes.shape({
        title: PropTypes.string,
        subTitle: PropTypes.string
      }),
      tags: PropTypes.arrayOf(PropTypes.object)
    }),
    published: PropTypes.string,
    updated: PropTypes.string,
    cardColor: PropTypes.string,
    descripton: PropTypes.string,
    introduction: PropTypes.string,
    image: PropTypes.string,
    goodFor: PropTypes.arrayOf(PropTypes.object),
    prosCons: PropTypes.shape({
      title: PropTypes.string,
      titleTOC: PropTypes.string,
      pros: PropTypes.arrayOf(PropTypes.object),
      cons: PropTypes.arrayOf(PropTypes.object)
    }),
    subscribeBlock: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      imageBaner: PropTypes.string
    }),
    cheetChat: PropTypes.shape({
      title: PropTypes.string,
      titleTOC: PropTypes.string
    }),
    whatIs: PropTypes.shape({
      title: PropTypes.string,
      titleTOC: PropTypes.string,
      text: PropTypes.string
    }),
    whereGetsItRight: PropTypes.shape({
      title: PropTypes.string,
      titleTOC: PropTypes.string,
      text: PropTypes.string
    }),
    whereCouldDoBetter: PropTypes.shape({
      title: PropTypes.string,
      titleTOC: PropTypes.string,
      text: PropTypes.string
    }),
    canYouReallyMakeMoneyOn: PropTypes.shape({
      title: PropTypes.string,
      titleTOC: PropTypes.string,
      text: PropTypes.string
    }),
    whatOtherPeopleAreSayingAbout: PropTypes.shape({
      title: PropTypes.string,
      titleTOC: PropTypes.string,
      text: PropTypes.string
    }),
    areThereOtherAppsLike: PropTypes.shape({
      id: PropTypes.number,
      text: PropTypes.string,
      title: PropTypes.string,
      titleTOC: PropTypes.string,
      investments: PropTypes.arrayOf(PropTypes.object)
    }),
    ourHotTakeOn: PropTypes.shape({
      title: PropTypes.string,
      titleTOC: PropTypes.string,
      text: PropTypes.string
    }),
    howDoISignUpFor: PropTypes.shape({
      title: PropTypes.string,
      titleTOC: PropTypes.string,
      faq: PropTypes.arrayOf(PropTypes.object)
    }),
    faq: PropTypes.shape({
      title: PropTypes.string,
      titleTOC: PropTypes.string,
      faq: PropTypes.arrayOf(PropTypes.object)
    }),
    platform: PropTypes.shape({
      slugPlatform: PropTypes.string
    }),
    investment: PropTypes.shape({
      id: PropTypes.string,
      website: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string
    }),
    featuredDeals: PropTypes.bool
  }),

  isLogged: PropTypes.bool,
  itemSource: PropTypes.string,
  authors: PropTypes.arrayOf(PropTypes.object),
  itemMedium: PropTypes.string,
  itemCampaign: PropTypes.string,
  reviews: PropTypes.arrayOf(PropTypes.object)
}

SoloPlatformReviewPage.defaultProps = {
  inIframe: false,
  review: {
    name: '',
    image: '',
    authors: [],
    slug: '',
    title: '',
    cardColor: '',
    published: '',
    updated: '',
    description: '',
    introduction: '',
    platform: {
      slugPlatform: ''
    },
    prosCons: {},
    ticker: {
      slug: '',
      className: ''
    },
    subscribeBlock: {
      title: '',
      description: '',
      imageBaner: ''
    },

    cheetChat: {},
    goodFor: [],
    whatIsPlatform: {},
    whereGetsItRight: {},
    whereCouldDoBetter: {},
    canYouReallyMakeMoneyOn: {},
    whatOtherPeopleAreSayingAbout: {},
    areThereOtherAppsLike: {},
    ourHotTakeOn: {},
    howDoISignUpFor: {},
    featuredDeals: false,
    faq: {},
    asset: {
      data: {
        title: '',
        subTitle: ''
      },
      tags: []
    },
    investment: {
      id: '',
      website: '',
      name: ''
    }
  },

  reviews: [],
  itemSource: '',
  itemMedium: '',
  itemCampaign: '',
  authors: [],
  isLogged: false
}

export default SoloPlatformReviewPage
